import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { GuideArticle, guideText, normalizeGuideSearch } from '../../content/adminGuide';
import { PageContainer, PageHeader } from '../layout/Page';

export default function GuideReader({ title, description, articles }: { title: string; description?: string; articles: GuideArticle[] }) {
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const requested = params.get('tema');
  const selected = articles.find(article => article.id === requested) || articles[0];
  const query = normalizeGuideSearch(search);
  const matches = articles.filter(article => normalizeGuideSearch(guideText(article)).includes(query));
  const heading = useRef<HTMLHeadingElement>(null);
  const previous = useRef(selected.id);
  useEffect(() => {
    if (previous.current !== selected.id) {
      previous.current = selected.id;
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView({ block: 'start' });
    }
  }, [selected.id]);
  return <PageContainer>
      <PageHeader title={title} description={description} />
      <div className="grid items-start gap-8 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
        <aside className="min-w-0 lg:sticky lg:top-24">
          <label htmlFor="guide-search" className="sr-only">Buscar en la guía</label>
          <input id="guide-search" type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar en la guía" className="mb-4 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:outline-blue-600" />
          <nav aria-label="Contenido de la guía" className="space-y-1">
            {matches.map(article => <Link key={article.id} to={`?tema=${article.id}`} aria-current={selected.id === article.id ? 'page' : undefined}
              className={`block border-l-2 px-3 py-2 text-sm focus-visible:outline-blue-600 ${selected.id === article.id ? 'border-blue-700 font-medium text-blue-800' : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900'}`}>
              {article.title}
            </Link>)}
          </nav>
          {query && <p role="status" className="mt-3 text-xs text-slate-500">{matches.length ? `${matches.length} temas encontrados` : 'No se encontraron temas.'}</p>}
        </aside>
        <article aria-labelledby="guide-article-title" className="min-w-0 border-t border-slate-200 pt-6 lg:border-t-0 lg:border-l lg:pl-10 lg:pt-0">
          <h2 ref={heading} tabIndex={-1} id="guide-article-title" className="scroll-mt-24 text-2xl font-semibold tracking-tight outline-none">{selected.title}</h2>
          <p className="mt-3 max-w-[68ch] text-base leading-7 text-slate-600">{selected.summary}</p>
          <div className="mt-8 space-y-8">
            {selected.blocks.map((block, i) => <section key={block.title} aria-labelledby={`guide-block-${selected.id}-${i}`}>
              <h3 id={`guide-block-${selected.id}-${i}`} className="mb-3 text-base font-semibold">{block.title}</h3>
              {block.paragraphs?.map(paragraph => <p key={paragraph} className="mt-3 max-w-[72ch] text-sm leading-7 text-slate-600">{paragraph}</p>)}
              {block.steps && <ol className="ml-5 max-w-[70ch] list-decimal space-y-3 text-sm leading-7 text-slate-600 marker:text-slate-400">{block.steps.map(step => <li key={step} className="pl-2">{step}</li>)}</ol>}
            </section>)}
          </div>
          {!!selected.links.length && <footer className="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-t border-slate-200 pt-5">{selected.links.map(link => <Link key={link.to} to={link.to} className="text-sm font-medium text-blue-700 underline-offset-4 hover:underline focus-visible:outline-blue-600">{link.label}</Link>)}</footer>}
        </article>
      </div>
  </PageContainer>;
}
